import {
  DESTINATIONS,
  type DestinationId,
} from '@/components/visitone/navigation-model'

const DESTINATION_BY_ROUTE = new Map(
  DESTINATIONS.map((destination) => [destination.route, destination.id]),
)

const DESTINATION_BY_ID = new Map(
  DESTINATIONS.map((destination) => [destination.id, destination.route]),
)

/** Deep-route prefixes map to the nearest built top-level destination. */
const ROUTE_PREFIX_TO_DESTINATION: Record<string, DestinationId> = {
  insights: 'insights',
  programme: 'programme',
  guests: 'guests',
  setup: 'setup',
  engagement: 'engagement',
  today: 'today',
  // No dedicated surfaces exist for these yet; route to the closest overview.
  tenants: 'insights',
  activity: 'insights',
}

export function destinationFromPath(pathname: string): DestinationId {
  const normalised = pathname.replace(/\/+$/, '') || '/'
  const exact = DESTINATION_BY_ROUTE.get(normalised)
  if (exact) return exact

  const segment = normalised.replace(/^\/+/, '').split(/[/?]/)[0]
  return ROUTE_PREFIX_TO_DESTINATION[segment] ?? 'today'
}

export function pathForDestination(id: DestinationId): string {
  return DESTINATION_BY_ID.get(id) ?? '/today'
}

export const SETUP_ORDER_TYPES_PATH = '/setup/ordering/order-types'
export const SETUP_LANDLORDS_PATH = '/setup/landlords'
export const SETUP_GUEST_DATA_ACCESS_PATH = '/setup/guest-data-access'
export const SETUP_PEOPLE_PATH = '/setup/people'
export const SETUP_PEOPLE_ROLES_PATH = '/setup/people/roles'
export const SETUP_PEOPLE_CHANGES_PATH = '/setup/people/changes'

export const GUESTS_PATH = '/guests'

/** A guest list's own URL (VIS-460), with an Arrivals day and venue when chosen (VIS-490). */
export function guestListPath(list: string, { date, venue }: { date?: string; venue?: string } = {}) {
  const query = new URLSearchParams({ list })
  if (date) query.set('date', date)
  if (venue && venue !== 'all') query.set('venue', venue)
  return `${GUESTS_PATH}?${query}`
}

/** A guest record's own URL (VIS-460). */
export const guestRecordPath = (guestId: string) => `${GUESTS_PATH}/${encodeURIComponent(guestId)}`

/** Deep routes that have their own screen. */
const BUILT_DEEP_PATHS = new Set([
  SETUP_ORDER_TYPES_PATH,
  SETUP_LANDLORDS_PATH,
  SETUP_GUEST_DATA_ACCESS_PATH,
  SETUP_PEOPLE_PATH,
  SETUP_PEOPLE_ROLES_PATH,
  SETUP_PEOPLE_CHANGES_PATH,
])

/** Prefixes whose every one-segment child is a built screen of the destination: `/guests/:guestId`. */
const BUILT_DEEP_PREFIXES: [string, DestinationId][] = [[`${GUESTS_PATH}/`, 'guests']]

function builtPrefixDestination(path: string): DestinationId | null {
  for (const [prefix, destination] of BUILT_DEEP_PREFIXES) {
    const rest = path.slice(prefix.length)
    if (path.startsWith(prefix) && rest && !rest.includes('/')) return destination
  }
  return null
}

/**
 * The destination of a built route, or null for any other path. Unlike
 * `destinationFromPath` there is no fallback, so an unknown route never
 * marks a rail item (VIS-329).
 */
export function routeDestination(pathname: string): DestinationId | null {
  const path = pathname.replace(/\/+$/, '') || '/'
  const exact = DESTINATION_BY_ROUTE.get(path)
  if (exact) return exact
  if (BUILT_DEEP_PATHS.has(path)) return 'setup'
  return builtPrefixDestination(path)
}

/**
 * Resolve an in-app path (including unfinished deep links) to the nearest
 * built route: the path itself when it has a screen, otherwise its
 * destination's route. Used by Today module actions that still emit paths
 * such as `/programme/:id` or `/setup/:topic`, and by palette entries. The
 * query is dropped, except a guest list's `?list=`, which is its own URL.
 */
export function nearestBuiltPath(route: string): string {
  const [pathAndQuery] = route.split('#')
  const [rawPath, query = ''] = pathAndQuery.split('?')
  const path = rawPath.replace(/\/+$/, '')
  if (BUILT_DEEP_PATHS.has(path) || builtPrefixDestination(path)) return path
  if (path === GUESTS_PATH) {
    const list = new URLSearchParams(query).get('list')
    return list ? guestListPath(list) : GUESTS_PATH
  }
  const destination = destinationFromPath(route)
  return pathForDestination(destination)
}
